import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { IDEAL_ASPECT_RATIO, preventEventPropagation } from '@bingads-webui-campaign-react/smart-component-util';
import {
  useIsComponentSelected,
  usePreviewPageContext,
  ComponentLoadingStatus,
} from '@bingads-webui-campaign-react/smart-page-context';
import { Loading } from '@bingads-webui-campaign-react/smart-component-primitive';

import { ImageCropperEditor } from './image-cropper-editor';
import { ImageCropperPreview } from './image-cropper-preview';

import { getStyles } from './styles';

const cropSettingsInPercentage = ({
  cropX,
  cropY,
  cropWidth,
  cropHeight,
}, imageWidth, imageHeight) => ({
  cropX: cropX / imageWidth,
  cropY: cropY / imageHeight,
  cropWidth: cropWidth / imageWidth,
  cropHeight: cropHeight / imageHeight,
});

const cropSettingsInPixels = ({
  cropX,
  cropY,
  cropWidth,
  cropHeight,
}, imageWidth, imageHeight) => ({
  cropX: cropX * imageWidth,
  cropY: cropY * imageHeight,
  cropWidth: cropWidth * imageWidth,
  cropHeight: cropHeight * imageHeight,
});

export const UnStyledImageCropper = (props) => {
  const {
    i18n,
    classes,
    src,
    imageWidth,
    imageHeight,
    cropSettings: initialCropSettings,
    isEditMode,
    isActive,
    setIsActive,
    onCropSettingsChange,
    isExternalSrc,
  } = props;

  const [cropSettings, setCropSettings] = useState({});

  const { elementsLoadingStatus } = usePreviewPageContext();

  const imageAspectRatio = imageHeight / imageWidth;
  const aspectRatiosProportion = imageAspectRatio / IDEAL_ASPECT_RATIO;
  const showEditMode = isEditMode && aspectRatiosProportion > 1;
  const triggerEditor = showEditMode && isActive;
  const cropperStyle = triggerEditor ? classes.imageCropperEditor : classes.imageCropperPreview;

  const selected = useIsComponentSelected('coverImage');

  useEffect(
    () => {
      setCropSettings(initialCropSettings
        ? cropSettingsInPercentage(initialCropSettings, imageWidth, imageHeight)
        : ({
          cropX: 0,
          cropY: 0,
          cropWidth: 1,
          cropHeight: triggerEditor ? (1 / aspectRatiosProportion) : 1,
        }));
    },
    [aspectRatiosProportion, imageHeight, imageWidth, initialCropSettings, triggerEditor]
  );

  const updateCropSettings = useCallback(
    (newCropSettings) => {
      setCropSettings(newCropSettings);
      onCropSettingsChange(cropSettingsInPixels(newCropSettings, imageWidth, imageHeight));
    },
    [imageHeight, imageWidth, onCropSettingsChange]
  );

  const activate = useCallback((e) => {
    setIsActive(true);
    if (selected) {
      preventEventPropagation(e);
    }
  }, [setIsActive, selected]);

  const CropperView = useMemo(
    () => (triggerEditor ? ImageCropperEditor : ImageCropperPreview),
    [triggerEditor]
  );

  return (
    <div className={classNames('image-cropper', cropperStyle)}>
      {
        elementsLoadingStatus.coverImage &&
          elementsLoadingStatus.coverImage.status === ComponentLoadingStatus.LOADING
          ? <Loading
            className={classes.previewLoading}
            label={elementsLoadingStatus.coverImage.reason}
          />
          :
          <CropperView
            i18n={i18n}
            classes={classes}
            src={src}
            cropSettings={cropSettings}
            aspectRatio={IDEAL_ASPECT_RATIO}
            onCropFinished={updateCropSettings}
            activate={activate}
            isInEditor={showEditMode}
            isExternalSrc={isExternalSrc}
          />
      }
    </div>
  );
};

UnStyledImageCropper.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    imageCropperEditor: PropTypes.string.isRequired,
    imageCropperPreview: PropTypes.string.isRequired,
  }).isRequired,
  src: PropTypes.string.isRequired,
  imageWidth: PropTypes.number.isRequired,
  imageHeight: PropTypes.number.isRequired,
  isEditMode: PropTypes.bool,
  isActive: PropTypes.bool.isRequired,
  setIsActive: PropTypes.func.isRequired,
  cropSettings: PropTypes.shape({}),
  onCropSettingsChange: PropTypes.func.isRequired,
  isExternalSrc: PropTypes.bool,
};

UnStyledImageCropper.defaultProps = {
  cropSettings: null,
  isEditMode: false,
  isExternalSrc: false,
};

export const ImageCropper = withDefaultStyles(UnStyledImageCropper, getStyles);
